/**
 * Home dashboard drag-and-drop lifecycle: blur inputs, tooltip seize/free, persist panel order,
 * and toolbox drop bridge after a successful reorder.
 */
import { inject, Injectable } from "@angular/core";
import { LoginStore } from "../../../../stores/login.store";
import { TooltipService } from "../../../../services/tooltip.service";
import { DASH_SORTABLE_DRAG_PREVIEW_CLASS } from "./dash.sortable.constants";
import { setDashPanelHeaderDragPreview } from "./dash.sortable.preview.util";
import { DashService } from "./dash.service";
import { DashPanelToolboxBridge } from "./dash.panel.toolbox.bridge";

@Injectable({ providedIn: "root" })
export class DashSortableService {
    private readonly loginStore = inject(LoginStore);
    private readonly dashService = inject(DashService);

    /** Sort/reorder is only available when the user is logged in. */
    isSortable(): boolean {
        return this.loginStore.loggedIn();
    }

    onDragStarted(dragRootElement: HTMLElement, isToolbox: boolean): void {
        document.querySelectorAll("input").forEach((node) => {
            if (node instanceof HTMLInputElement) {
                node.blur();
            }
        });
        TooltipService.setSeized();

        requestAnimationFrame(() => {
            const previewElement = document.querySelector(`.${DASH_SORTABLE_DRAG_PREVIEW_CLASS}`);
            if (previewElement instanceof HTMLElement) {
                setDashPanelHeaderDragPreview(previewElement, dragRootElement, isToolbox);
            }
        });
    }

    onDragEnded(): void {
        TooltipService.setFree();
    }

    /**
     * Persists column layout before {@link DashPanelToolboxBridge.invoke}: `panel-items.json` is keyed by
     * panel placement; invoking the panel load while `putPanelsOrder` is still in flight can return an empty
     * column (wrong "no starred resources" until a later collapse/expand refetch).
     */
    persistOrderAfterDrop(toolboxDropPanelId?: number): void {
        void this.dashService
            .get()
            .then((dash) => this.dashService.putPanelsOrder(dash))
            .finally(() => {
                if (toolboxDropPanelId != null) {
                    DashPanelToolboxBridge.invoke(toolboxDropPanelId);
                }
            });
    }
}
